// TripHistoryClient.jsx - Affiche l'historique des trajets pour les passagers
import React, { useState, useEffect, useCallback } from 'react';
import InvoiceLink from './market/InvoiceLink';
import { MapPin, Star, Download, Filter, ChevronDown } from 'lucide-react';
import apiService from './apiService';

export default function TripHistoryClient({ userId, onRateDriver }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedTripId, setExpandedTripId] = useState(null);

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch both regular trips and taxi rides in parallel
      const [tripsResponse, taxiResponse] = await Promise.all([
        apiService.get(`/trips/passenger/${userId}?filter=${filter}&limit=50`, { userType: 'client' }).catch(() => ({ success: false })),
        apiService.get(`/taxi/client/${userId}/history`).catch(() => ({ success: false }))
      ]);

      let allTrips = [];

      // Regular bus/route trips
      if (tripsResponse.success) {
        allTrips = (tripsResponse.trips || []).map(t => ({ ...t, trip_type: 'bus' }));
      }

      // Taxi rides — convert to trip-like format
      if (taxiResponse.success && taxiResponse.rides) {
        const taxiTrips = taxiResponse.rides
          .filter(r => filter === 'all' || (filter === 'completed' && r.status === 'completed') || (filter === 'cancelled' && ['cancelled_client', 'cancelled_driver'].includes(r.status)))
          .map(ride => ({
            trip_id: ride.ride_id,
            trip_type: 'taxi',
            status: ride.status === 'completed' ? 'completed' : 'cancelled',
            created_at: ride.created_at,
            driver_name: ride.driver_name || 'Chauffeur taxi',
            driver_photo: null,
            start_location: { name: ride.pickup?.address },
            route_name: `${ride.pickup?.address} → ${ride.dropoff?.address}`,
            distance_km: ride.distance_km,
            vehicle_info: null,
            rating: ride.rating_by_client ? { score: ride.rating_by_client, comment: ride.comment_by_client } : null,
            price: ride.final_price || ride.estimated_price,
            currency: ride.currency || 'CDF'
          }));
        allTrips = [...allTrips, ...taxiTrips];
      }

      // Sort by date descending
      allTrips.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTrips(allTrips);
    } catch (error) {
      console.error('Erreur chargement trajets:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, filter]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'cancelled': return '#DC2626';
      default: return '#5C636E';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Complété';
      case 'cancelled': return 'Annulé';
      case 'cancelled_client': return 'Annulé (client)';
      case 'cancelled_driver': return 'Annulé (chauffeur)';
      default: return 'Inconnu';
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement des trajets...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📍 Historique de mes trajets</h2>
        <p style={styles.subtitle}>Tous vos trajets effectués</p>
      </div>

      {/* Filtre */}
      <div style={styles.filterBar}>
        <Filter size={18} style={{color: '#5C636E'}} />
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Tous les trajets</option>
          <option value="completed">Complétés</option>
          <option value="cancelled">Annulés</option>
        </select>
      </div>

      {/* Stats Résumées */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Total trajets</p>
          <p style={styles.statValue}>{trips.length}</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Trajets complétés</p>
          <p style={styles.statValue}>{trips.filter(t => t.status === 'completed').length}</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}>Trajets annulés</p>
          <p style={styles.statValue}>{trips.filter(t => t.status === 'cancelled').length}</p>
        </div>
      </div>

      {/* Liste des Trajets */}
      <div style={styles.tripsContainer}>
        {trips.length === 0 ? (
          <div style={styles.emptyState}>
            <MapPin size={48} style={{color: '#DCE0E6', marginBottom: '1rem'}} />
            <p style={styles.emptyText}>Aucun trajet trouvé</p>
          </div>
        ) : (
          trips.map((trip) => (
            <div key={trip.trip_id} style={styles.tripCard}>
              <div style={styles.tripHeader}>
                <div style={styles.tripInfo}>
                  <div style={styles.tripDates}>
                    <span style={styles.date}>{formatDate(trip.created_at)}</span>
                    {trip.trip_type === 'taxi' && (
                      <span style={{ padding: '2px 8px', borderRadius: '8px', background: '#fef3c7', color: '#92400e', fontSize: '0.7rem', fontWeight: '600' }}>🚖 Taxi</span>
                    )}
                    {trip.trip_type === 'bus' && (
                      <span style={{ padding: '2px 8px', borderRadius: '8px', background: '#FEE9E2', color: '#3730a3', fontSize: '0.7rem', fontWeight: '600' }}>🚌 Bus</span>
                    )}
                    <span style={{
                      ...styles.status,
                      background: getStatusColor(trip.status),
                      color: 'white'
                    }}>
                      {getStatusLabel(trip.status)}
                    </span>
                  </div>
                  {trip.price && (
                    <div style={{ fontWeight: '700', color: '#C9350F', fontSize: '0.9rem', marginBottom: '4px' }}>
                      {trip.price?.toLocaleString('fr-FR')} {trip.currency || 'CDF'}
                    </div>
                  )}
                  {trip.trip_type === 'taxi' && trip.status === 'completed' && <InvoiceLink type="ride" id={trip.trip_id} compact />}
                  <div style={styles.driverInfo}>
                    {trip.driver_photo && (
                      <img src={trip.driver_photo} alt={trip.driver_name} style={styles.driverPhoto} />
                    )}
                    <div>
                      <p style={styles.driverName}>{trip.driver_name}</p>
                      {trip.vehicle_info && (
                        <p style={styles.vehicleInfo}>
                          {trip.vehicle_info.brand} {trip.vehicle_info.model}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div style={styles.tripMeta}>
                  <button
                    style={{...styles.expandBtn, transform: expandedTripId === trip.trip_id ? 'rotate(180deg)' : 'rotate(0deg)'}}
                    onClick={() => setExpandedTripId(expandedTripId === trip.trip_id ? null : trip.trip_id)}
                  >
                    <ChevronDown size={20} />
                  </button>
                </div>
              </div>

              {expandedTripId === trip.trip_id && (
                <div style={styles.tripDetails}>
                  {/* Localisation */}
                  <div style={styles.detailSection}>
                    <h4 style={styles.detailTitle}>🗺️ Trajet</h4>
                    <div style={styles.locationInfo}>
                      <div style={styles.location}>
                        <span style={styles.locationLabel}>Départ</span>
                        <p style={styles.locationText}>{trip.start_location?.name || 'Kinshasa Centre'}</p>
                      </div>
                      {trip.route_name && (
                        <div style={styles.location}>
                          <span style={styles.locationLabel}>Route</span>
                          <p style={styles.locationText}>{trip.route_name}</p>
                        </div>
                      )}
                      {trip.distance_km && (
                        <div style={styles.location}>
                          <span style={styles.locationLabel}>Distance</span>
                          <p style={styles.locationText}>{trip.distance_km} km</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Avis */}
                  {trip.rating && (
                    <div style={styles.detailSection}>
                      <h4 style={styles.detailTitle}>⭐ Avis</h4>
                      <div style={styles.ratingBox}>
                        <div style={styles.ratingScore}>
                          {'⭐'.repeat(trip.rating.score)} ({trip.rating.score}/5)
                        </div>
                        {trip.rating.comment && (
                          <p style={styles.ratingComment}>{trip.rating.comment}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={styles.tripActions}>
                    <button style={styles.detailsBtn}>
                      <Download size={16} />
                      Détails
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <style jsx>{`
        ${Object.entries(styles).map(([key, value]) => {
          if (typeof value === 'object') {
            return `.${key} { ${Object.entries(value).map(([k, v]) => `${k}: ${v};`).join(' ')} }`;
          }
          return '';
        }).join('\n')}
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '1.5rem',
    background: '#FFFFFF',
    minHeight: '100vh'
  },
  header: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: '700',
    color: '#1C1C1C',
    margin: '0 0 0.5rem 0'
  },
  subtitle: {
    color: '#5C636E',
    margin: '0',
    fontSize: '0.95rem'
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: 'white',
    borderRadius: '12px',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  filterSelect: {
    border: 'none',
    background: 'transparent',
    fontSize: '0.95rem',
    color: '#414852',
    cursor: 'pointer',
    outline: 'none',
    flex: 1
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem'
  },
  statCard: {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  statLabel: {
    fontSize: '0.85rem',
    color: '#5C636E',
    margin: '0',
    textTransform: 'uppercase',
    fontWeight: '600'
  },
  statValue: {
    fontSize: '1.8rem',
    fontWeight: '700',
    color: '#1C1C1C',
    margin: '0.5rem 0 0 0'
  },
  tripsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1.5rem',
    background: 'white',
    borderRadius: '16px',
    color: '#9AA0A8'
  },
  emptyText: {
    fontSize: '1rem',
    color: '#5C636E',
    margin: '0'
  },
  tripCard: {
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.2s'
  },
  tripHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem',
    borderBottom: '1px solid #E9EBEF',
    cursor: 'pointer'
  },
  tripInfo: {
    flex: 1
  },
  tripDates: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem'
  },
  date: {
    fontSize: '0.85rem',
    color: '#5C636E',
    fontWeight: '500'
  },
  status: {
    fontSize: '0.75rem',
    fontWeight: '700',
    padding: '0.35rem 0.75rem',
    borderRadius: '999px',
    textTransform: 'uppercase'
  },
  driverInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  driverPhoto: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    objectFit: 'cover'
  },
  driverName: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1C1C1C',
    margin: '0'
  },
  vehicleInfo: {
    fontSize: '0.85rem',
    color: '#5C636E',
    margin: '0.25rem 0 0 0'
  },
  tripMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    minWidth: '80px'
  },
  expandBtn: {
    background: '#F4F5F7',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s',
    color: '#5C636E'
  },
  tripDetails: {
    padding: '1.25rem',
    background: '#FFFFFF'
  },
  detailSection: {
    marginBottom: '1.5rem'
  },
  detailTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#414852',
    margin: '0 0 0.75rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  locationInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem'
  },
  location: {
    background: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    borderLeft: '3px solid #FFB900'
  },
  locationLabel: {
    fontSize: '0.75rem',
    color: '#5C636E',
    textTransform: 'uppercase',
    fontWeight: '600',
    display: 'block',
    marginBottom: '0.25rem'
  },
  locationText: {
    fontSize: '0.95rem',
    color: '#1C1C1C',
    margin: '0',
    fontWeight: '500'
  },
  ratingBox: {
    background: 'white',
    padding: '1rem',
    borderRadius: '8px',
    borderLeft: '3px solid #FFB900'
  },
  ratingScore: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#FFB900',
    marginBottom: '0.5rem'
  },
  ratingComment: {
    fontSize: '0.9rem',
    color: '#5C636E',
    margin: '0',
    fontStyle: 'italic'
  },
  tripActions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end'
  },
  rateBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    background: '#FFB900',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    transition: 'all 0.2s'
  },
  detailsBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    background: '#E9EBEF',
    color: '#414852',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    transition: 'all 0.2s'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    color: '#5C636E'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #E9EBEF',
    borderTop: '3px solid #FFB900',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '1rem'
  }
};
